import React, { useState, useRef, useEffect, useCallback, useReducer } from "react";
import "./AIChatWidget.css";
import "./animations.css";
import ScrollDownButton from "../ScrollDownButton/ScrollDownButton";
import { stripPatternFromString } from "../../lib/utils";
import { formatRelativeTime, updateAllTimestamps } from "../../lib/timeUtils";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import ThemeToggle from "./themes/ThemeToggle";
import { ThemeMode, themeColors, themeToCssVars, getSystemTheme } from "./themes/themeConfig";
import { WelcomePage } from "../WelcomePage/WelcomePage";
import { retryWithBackoff } from "../../utils/errorHandling";
import { useStorage } from "../../lib/storage/StorageContext";
import { isOnline, addNetworkStatusListener } from "../../lib/utils/networkUtils";
import { logger } from "../../lib/utils/loggingUtils";
import { handleStorageFailure } from "../../utils/storageHelpers";

function buildWidgetApiUrl(apiBaseUrl: string, appId: string, path: string) {
  const normalizedBaseUrl = apiBaseUrl.replace(/\/+$/, "");
  const encodedOrganizationId = encodeURIComponent(appId);
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBaseUrl}/v1/organizations/${encodedOrganizationId}/ai-widget${normalizedPath}`;
}

function generateRandomIdSuffix(): string {
  // Prefer crypto-strong identifiers in the browser.
  if (typeof globalThis !== "undefined") {
    const cryptoObject = (globalThis as any).crypto as Crypto | undefined;
    if (cryptoObject?.randomUUID) return cryptoObject.randomUUID();
    if (cryptoObject?.getRandomValues) {
      const bytes = new Uint8Array(16);
      cryptoObject.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  }

  // Fallback for very old browsers (non-security critical usage: local UI IDs).
  return `${Date.now()}_${String(Math.floor(Date.now() % 1000000))}`;
}

function resolveThemeColors(theme: ThemeMode, primaryColor: string, secondaryColor: string) {
  switch (theme) {
    case "light":
      return themeColors.light(primaryColor, secondaryColor);
    case "dark":
      return themeColors.dark(primaryColor, secondaryColor);
    default:
      return themeColors.light(primaryColor, secondaryColor);
  }
}

function isSafeExternalHref(href: string): boolean {
  try {
    const parsedUrl = new URL(href);
    return parsedUrl.protocol === "https:" || parsedUrl.protocol === "http:";
  } catch {
    return false;
  }
}

function renderSafeFormattedText(rawText: string): React.ReactNode {
  const text = stripPatternFromString(rawText);
  const tokenPattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

  function renderInlineTokens(lineText: string, lineIndex: number) {
    const parts = lineText.split(tokenPattern).filter((part) => part.length > 0);

    return parts.map((part, partIndex) => {
      const key = `${lineIndex}:${partIndex}`;

      if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
        return <strong key={key}>{part.slice(2, -2)}</strong>;
      }

      if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
        return <em key={key}>{part.slice(1, -1)}</em>;
      }

      if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
        return <code key={key}>{part.slice(1, -1)}</code>;
      }

      if (part.startsWith("[") && part.includes("](") && part.endsWith(")")) {
        const labelEndIndex = part.indexOf("](");
        const label = part.slice(1, labelEndIndex);
        const href = part.slice(labelEndIndex + 2, -1);

        if (isSafeExternalHref(href)) {
          return (
            <a key={key} href={href} target="_blank" rel="noopener noreferrer">
              {label}
            </a>
          );
        }

        return <span key={key}>{label}</span>;
      }

      return <React.Fragment key={key}>{part}</React.Fragment>;
    });
  }

  const lines = text.split("\n");
  return (
    <>
      {lines.map((line, lineIndex) => (
        <React.Fragment key={String(lineIndex)}>
          {renderInlineTokens(line, lineIndex)}
          {lineIndex < lines.length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </>
  );
}

// Props interface for the AIChatWidget component
export interface AIChatWidgetProps {
  botName?: string; // Display name of the chatbot
  botLogo?: string; // URL or base64 image for bot logo/mascot
  primaryColor?: string; // Primary color theme for the widget
  secondaryColor?: string; // Secondary color for buttons and accents
  position?: "left" | "right"; // Position of the widget on the screen
  initialMessages?: string[]; // Suggested initial messages/questions from API
  appId: string; // Only required prop from client
  token?: string; // Optional authentication token
  apiBaseUrl: string; // Base URL for API requests
  theme?: ThemeMode; // Optional theme prop
  onThemeChange?: (theme: ThemeMode) => void; // Optional callback for theme changes
  useShadowDom?: boolean; // Whether the component is rendered within Shadow DOM
}

// Interface for chat messages
export interface Message {
  id?: string; // Unique ID for deduplication
  text: string; // Content of the message
  type: "bot" | "user"; // Sender type to differentiate styling
  timestamp: Date; // Time when message was sent
  threadId?: string | null; // Allow null type for threadId
}

// Interface for local storage data
interface ChatStorage {
  threadId: string | null;
  messages: StorageMessage[];
}

// Message format for storage (using string for timestamp)
interface StorageMessage {
  text: string;
  type: "bot" | "user";
  timestamp: string;
  threadId?: string | null;
}

// These functions are now handled by the StorageContext

// Add interface for message group
interface MessageGroup {
  type: "bot" | "user";
  messages: Message[];
  timestamp: Date;
}

// State management interfaces
interface WidgetUIState {
  isOpen: boolean;
  isClosing: boolean;
  isWide: boolean;
  showClearConfirm: boolean;
  showWelcomePage: boolean;
  copiedMessageId: string | null;
  theme: ThemeMode;
  enableThemeToggle: boolean;
  enableAttribution: boolean;
  isOffline: boolean;
}

interface ChatState {
  inputValue: string;
  messages: Message[];
  threadId: string | null;
  isAITyping: boolean;
}

interface StorageState {
  useMemoryFallback: boolean;
  memoryThreadId: string | null;
  memoryMessages: Message[];
  storageFailCount: number;
  isInitialized: boolean;
}

// Action types for reducers
type WidgetUIAction =
  | { type: "SET_IS_OPEN"; payload: boolean }
  | { type: "SET_IS_CLOSING"; payload: boolean }
  | { type: "SET_IS_WIDE"; payload: boolean }
  | { type: "TOGGLE_IS_WIDE" }
  | { type: "SET_SHOW_CLEAR_CONFIRM"; payload: boolean }
  | { type: "SET_SHOW_WELCOME_PAGE"; payload: boolean }
  | { type: "SET_COPIED_MESSAGE_ID"; payload: string | null }
  | { type: "SET_THEME"; payload: ThemeMode }
  | { type: "SET_ENABLE_THEME_TOGGLE"; payload: boolean }
  | { type: "SET_ENABLE_ATTRIBUTION"; payload: boolean }
  | { type: "SET_IS_OFFLINE"; payload: boolean };

type ChatAction =
  | { type: "SET_INPUT_VALUE"; payload: string }
  | { type: "SET_MESSAGES"; payload: Message[] }
  | { type: "ADD_MESSAGE"; payload: Message }
  | { type: "SET_THREAD_ID"; payload: string | null }
  | { type: "SET_IS_AI_TYPING"; payload: boolean }
  | { type: "CLEAR_MESSAGES" }
  | { type: "UPDATE_MESSAGE_TEXT"; payload: { id: string; text: string } };

type StorageAction =
  | { type: "SET_USE_MEMORY_FALLBACK"; payload: boolean }
  | { type: "SET_MEMORY_THREAD_ID"; payload: string | null }
  | { type: "SET_MEMORY_MESSAGES"; payload: Message[] }
  | { type: "SET_STORAGE_FAIL_COUNT"; payload: number }
  | { type: "INCREMENT_STORAGE_FAIL_COUNT" }
  | { type: "SET_IS_INITIALIZED"; payload: boolean };

// Reducers
function widgetUIReducer(state: WidgetUIState, action: WidgetUIAction): WidgetUIState {
  switch (action.type) {
    case "SET_IS_OPEN":
      return { ...state, isOpen: action.payload };
    case "SET_IS_CLOSING":
      return { ...state, isClosing: action.payload };
    case "SET_IS_WIDE":
      return { ...state, isWide: action.payload };
    case "TOGGLE_IS_WIDE":
      return { ...state, isWide: !state.isWide };
    case "SET_SHOW_CLEAR_CONFIRM":
      return { ...state, showClearConfirm: action.payload };
    case "SET_SHOW_WELCOME_PAGE":
      return { ...state, showWelcomePage: action.payload };
    case "SET_COPIED_MESSAGE_ID":
      return { ...state, copiedMessageId: action.payload };
    case "SET_THEME":
      return { ...state, theme: action.payload };
    case "SET_ENABLE_THEME_TOGGLE":
      return { ...state, enableThemeToggle: action.payload };
    case "SET_ENABLE_ATTRIBUTION":
      return { ...state, enableAttribution: action.payload };
    case "SET_IS_OFFLINE":
      return { ...state, isOffline: action.payload };
    default:
      return state;
  }
}

/**
 * Inserts a message into a sorted messages array maintaining chronological order.
 * Uses binary search for O(log n) insertion when messages are out of order,
 * or O(1) append when the new message is after all existing messages.
 */
function insertMessageSorted(messages: Message[], newMessage: Message): Message[] {
  const newTimestamp = newMessage.timestamp.getTime();

  // Fast path: if array is empty or new message is after all existing messages, just append
  if (messages.length === 0) {
    return [newMessage];
  }

  const lastMessage = messages[messages.length - 1];
  if (lastMessage && newTimestamp >= lastMessage.timestamp.getTime()) {
    return [...messages, newMessage];
  }

  // Binary search for insertion point
  let left = 0;
  let right = messages.length;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midMessage = messages[mid];
    if (!midMessage) {
      break;
    }
    const midTimestamp = midMessage.timestamp.getTime();

    if (midTimestamp < newTimestamp) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // Insert at the found position
  const result = [...messages];
  result.splice(left, 0, newMessage);
  return result;
}

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "SET_INPUT_VALUE":
      return { ...state, inputValue: action.payload };
    case "SET_MESSAGES":
      return { ...state, messages: action.payload };
    case "ADD_MESSAGE":
      return {
        ...state,
        messages: insertMessageSorted(state.messages, action.payload),
      };
    case "SET_THREAD_ID":
      return { ...state, threadId: action.payload };
    case "SET_IS_AI_TYPING":
      return { ...state, isAITyping: action.payload };
    case "CLEAR_MESSAGES":
      return { ...state, messages: [] };
    case "UPDATE_MESSAGE_TEXT": {
      const { id, text } = action.payload;
      logger.log("[REDUCER] UPDATE_MESSAGE_TEXT:", {
        id,
        textLength: text.length,
        messageCount: state.messages.length,
      });
      const updatedMessages = state.messages.map((msg) => {
        if (msg.id === id) {
          logger.log(
            "[REDUCER] Found message to update, old text length:",
            msg.text.length,
            "new:",
            text.length,
          );
          return { ...msg, text, timestamp: new Date() };
        }
        return msg;
      });
      logger.log("[REDUCER] Updated messages count:", updatedMessages.length);
      return {
        ...state,
        messages: updatedMessages,
      };
    }
    default:
      return state;
  }
}

function storageReducer(state: StorageState, action: StorageAction): StorageState {
  switch (action.type) {
    case "SET_USE_MEMORY_FALLBACK":
      return { ...state, useMemoryFallback: action.payload };
    case "SET_MEMORY_THREAD_ID":
      return { ...state, memoryThreadId: action.payload };
    case "SET_MEMORY_MESSAGES":
      return { ...state, memoryMessages: action.payload };
    case "SET_STORAGE_FAIL_COUNT":
      return { ...state, storageFailCount: action.payload };
    case "INCREMENT_STORAGE_FAIL_COUNT": {
      const newCount = state.storageFailCount + 1;
      const shouldSwitchToMemory = newCount >= 3 && !state.useMemoryFallback;
      return {
        ...state,
        storageFailCount: newCount,
        useMemoryFallback: shouldSwitchToMemory ? true : state.useMemoryFallback,
      };
    }
    case "SET_IS_INITIALIZED":
      return { ...state, isInitialized: action.payload };
    default:
      return state;
  }
}

/**
 * AIChatWidget Component
 * A floating chat widget that can be integrated into any website
 */
const AIChatWidget: React.FC<AIChatWidgetProps> = ({
  botName = "AI Assistant",
  botLogo,
  primaryColor = "#007bff",
  secondaryColor = "#007bff", // Default secondary color
  position = "right",
  initialMessages = [], // Default to empty array
  appId,
  token,
  apiBaseUrl,
  theme: initialTheme,
  onThemeChange,
  useShadowDom,
}) => {
  // Log initial props received
  logger.log("🚀 AIChatWidget initialized with:", {
    botName,
    primaryColor,
    secondaryColor,
    position,
    initialMessagesCount: initialMessages?.length,
    initialMessages,
    appId,
    apiBaseUrl,
  });

  // Get storage context
  const storage = useStorage();

  // Debug storage initialization
  logger.log("AIChatWidget: Storage context initialized with:", {
    isInitialized: storage.isInitialized,
    isIndexedDBSupported: storage.isIndexedDBSupported,
    hasSaveMessages: typeof storage.saveMessages === "function",
    hasSaveThreadId: typeof storage.saveThreadId === "function",
    hasLoadMessages: typeof storage.loadMessages === "function",
    hasGetThreadId: typeof storage.getThreadId === "function",
  });

  // State management - consolidated into grouped reducers
  const [uiState, dispatchUI] = useReducer(widgetUIReducer, {
    isOpen: false,
    isClosing: false,
    isWide: false,
    showClearConfirm: false,
    showWelcomePage: true,
    copiedMessageId: null,
    theme: initialTheme || getSystemTheme(),
    enableThemeToggle: false,
    enableAttribution: true,
    isOffline: !isOnline(),
  });

  const [chatState, dispatchChat] = useReducer(chatReducer, {
    inputValue: "",
    messages: [],
    threadId: null,
    isAITyping: false,
  });

  const [storageState, dispatchStorage] = useReducer(storageReducer, {
    useMemoryFallback: false,
    memoryThreadId: null,
    memoryMessages: [],
    storageFailCount: 0,
    isInitialized: false,
  });

  // Refs for DOM manipulation
  const chatButtonRef = useRef<HTMLButtonElement>(null); // Reference to chat toggle button
  const messagesEndRef = useRef<HTMLDivElement>(null); // Reference to messages end div for scrolling
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null); // Reference to input field for auto-focus
  const chatWindowRef = useRef<HTMLDivElement>(null); // Reference to chat window for transition events

  // Debounce configuration for message saving
  // Prevents race conditions when messages update rapidly (e.g., during streaming)
  // by batching saves and only persisting after a short idle period per threadId
  // Debounce delay for saving messages to storage
  // Higher value (2000ms) ensures we don't save incomplete streaming messages
  const MESSAGE_SAVE_DEBOUNCE_MS = 2000;
  const saveMessagesDebounceTimersRef = useRef<Map<string | null, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  // Function to save messages to memory fallback
  const saveMessagesToMemory = useCallback((msgs: Message[]) => {
    logger.log("AIChatWidget: Saving messages to memory fallback");
    dispatchStorage({ type: "SET_MEMORY_MESSAGES", payload: msgs });
  }, []);

  // Function to save threadId to memory fallback
  const saveThreadIdToMemory = useCallback((id: string) => {
    logger.log("AIChatWidget: Saving threadId to memory fallback:", id);
    dispatchStorage({ type: "SET_MEMORY_THREAD_ID", payload: id });
    dispatchChat({ type: "SET_THREAD_ID", payload: id }); // Ensure thread ID is also set in the component state
  }, []);

  // Use refs to hold latest state for use in async callbacks without adding dependencies
  const storageStateRef = useRef(storageState);
  const uiStateRef = useRef(uiState);

  // Keep refs updated
  useEffect(() => {
    storageStateRef.current = storageState;
    uiStateRef.current = uiState;
  }, [storageState, uiState]);

  // Function to check and update storage fail count
  const checkStorageOperation = useCallback(
    (success: boolean, operation: string) => {
      if (!success) {
        logger.warn(`AIChatWidget: Storage operation ${operation} failed`);
        // Use ref to get latest state
        handleStorageFailure(storageStateRef.current, dispatchStorage, logger);
        return false;
      }
      return true;
    },
    [dispatchStorage], // storageState dependency removed
  );

  // Add a utility function to directly verify storage readiness
  const verifyStorageReadiness = useCallback(
    async (operation: string): Promise<boolean> => {
      logger.log(`AIChatWidget: Verifying storage readiness for ${operation}`);

      // Check if storage exists
      if (!storage) {
        logger.warn(`AIChatWidget: Storage object doesn't exist for ${operation}`);
        return false;
      }

      // Check if the specific method exists - map operation names to actual method names
      let methodName = "";
      switch (operation) {
        case "saveMessage":
          methodName = "saveMessages";
          break;
        case "saveThreadId":
          methodName = "saveThreadId";
          break;
        case "getThreadId":
          methodName = "getThreadId";
          break;
        case "loadMessage":
          methodName = "loadMessages";
          break;
        case "hasExistingConversation":
          methodName = "hasExistingConversation";
          break;
        default:
          methodName = operation;
      }

      const method = (storage as any)[methodName];

      if (typeof method !== "function") {
        logger.warn(`AIChatWidget: Storage method ${methodName} not available for ${operation}`);
        return false;
      }

      // Log detailed info for debugging
      logger.log(`AIChatWidget: Storage status for ${operation}:`, {
        storageExists: !!storage,
        methodExists: !!method,
        isInitialized: storage.isInitialized,
        indexedDBStatus: storage.isIndexedDBSupported,
        useMemoryFallback: storageState.useMemoryFallback,
      });

      // Check if we're forcing memory fallback
      // Use ref to check current memory fallback state
      if (storageStateRef.current.useMemoryFallback) {
        logger.log(
          `AIChatWidget: Storage readiness check failed because useMemoryFallback=${storageStateRef.current.useMemoryFallback}`,
        );
        return false;
      }

      logger.log(`AIChatWidget: Storage is ready for ${operation}`);
      return true;
    },
    [storage], // storageState.useMemoryFallback dependency removed
  );

  // Offline message handling is disabled in this version

  /**
   * Scrolls the chat window to the latest message
   * Uses smooth scrolling for better UX
   */
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Auto-scroll when new messages are added
  useEffect(() => {
    scrollToBottom();

    // After scrolling and rendering, trigger a scroll event to update the ScrollDownButton visibility
    // The timeout defers the scroll dispatch until the DOM has updated, ensuring newly rendered
    // messages are present and ScrollDownButton visibility is recalculated. The 100ms delay
    // allows layout/paint to complete and avoids race conditions. If possible, this should be
    // replaced with a MutationObserver or requestAnimationFrame when refactoring.
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.dispatchEvent(new Event("scroll"));
      }
    }, 100);
  }, [chatState.messages]);

  // Initialize component and load data from storage
  useEffect(() => {
    const initializeWidget = async () => {
      try {
        logger.log("AIChatWidget: Initializing widget. Starting storage status check...");

        // First, check if storage is properly initialized
        if (!storage || !storage.isInitialized) {
          logger.warn(
            "AIChatWidget: Storage not properly initialized, switching to memory-only mode",
          );
          dispatchStorage({ type: "SET_USE_MEMORY_FALLBACK", payload: true });
          dispatchStorage({ type: "SET_IS_INITIALIZED", payload: true });
          dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: true });
          return;
        }

        logger.log("AIChatWidget: Storage is available and initialized:", {
          isIndexedDBSupported: storage.isIndexedDBSupported,
          hasSaveMessages: !!storage.saveMessages,
          hasSaveThreadId: !!storage.saveThreadId,
          hasLoadMessages: !!storage.loadMessages,
        });

        // Reset memory fallback in case it was set in a previous session
        dispatchStorage({ type: "SET_USE_MEMORY_FALLBACK", payload: false });

        // Get thread ID from storage with error handling
        let storedThreadId = null;
        try {
          logger.log("AIChatWidget: Getting thread ID from storage");
          storedThreadId = await storage.getThreadId();
          logger.log("AIChatWidget: Got thread ID from storage:", storedThreadId);
        } catch (storageError) {
          logger.warn(
            "Failed to get thread ID from storage, continuing with new session:",
            storageError,
          );
        }

        // If we have a valid threadId
        if (storedThreadId) {
          dispatchChat({ type: "SET_THREAD_ID", payload: storedThreadId });
          saveThreadIdToMemory(storedThreadId);

          // Load messages for this thread with error handling
          try {
            const storedMessages = await storage.loadMessages(storedThreadId);

            // If we have stored messages, use them
            if (storedMessages && storedMessages.length > 0) {
              dispatchChat({ type: "SET_MESSAGES", payload: storedMessages });
              saveMessagesToMemory(storedMessages);
              dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: false });
            } else {
              // If we have a thread ID but no messages, show welcome back message
              const welcomeBackMessage = {
                id: `bot_welcome_back_${generateRandomIdSuffix()}`,
                text: "Welcome back! Continuing our previous conversation...",
                type: "bot" as const,
                timestamp: new Date(),
                threadId: storedThreadId,
              };

              // Set welcome message with sorting (though there's only one message here)
              const sortedMessages = [welcomeBackMessage].sort(
                (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
              );
              dispatchChat({ type: "SET_MESSAGES", payload: sortedMessages });
              saveMessagesToMemory([welcomeBackMessage]);
              dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: false });
            }
          } catch (messagesError) {
            logger.warn("Failed to load messages, starting fresh:", messagesError);
            dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: true });
          }
        } else {
          // Check if there's an existing conversation
          try {
            const hasConversation = await storage.hasExistingConversation();
            dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: !hasConversation });
          } catch (convError) {
            logger.warn(
              "Failed to check for existing conversation, showing welcome page:",
              convError,
            );
            dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: true });
          }
        }

        logger.log("🏠 Welcome page visibility check:", {
          showWelcomePage: uiState.showWelcomePage,
          hasInitialMessages: initialMessages && initialMessages.length > 0,
          threadId: storedThreadId,
        });

        dispatchStorage({ type: "SET_IS_INITIALIZED", payload: true });
      } catch (error) {
        logger.error("Error initializing widget:", error);
        // Still mark as initialized and show welcome page to prevent blocking UI
        dispatchStorage({ type: "SET_IS_INITIALIZED", payload: true });
        dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: true });
        // Switch to memory-only mode if there was an error
        dispatchStorage({ type: "SET_USE_MEMORY_FALLBACK", payload: true });
      }
    };

    initializeWidget();

    // Set up network status listener
    const cleanup = addNetworkStatusListener(
      () => {
        dispatchUI({ type: "SET_IS_OFFLINE", payload: false });
        logger.log("AIChatWidget: Back online");
      },
      () => dispatchUI({ type: "SET_IS_OFFLINE", payload: true }),
    );

    return cleanup;
  }, [
    storage,
    initialMessages,
    verifyStorageReadiness,
    checkStorageOperation,
    saveMessagesToMemory,
    saveThreadIdToMemory,
  ]);

  // Debounced save function that batches rapid message updates per threadId
  const debouncedSaveMessages = useCallback(
    (threadId: string | null, messages: Message[]) => {
      // Clear any existing timer for this threadId
      const existingTimer = saveMessagesDebounceTimersRef.current.get(threadId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set a new timer to save after the debounce period
      const timer = setTimeout(async () => {
        // Remove timer from map when it executes
        saveMessagesDebounceTimersRef.current.delete(threadId);

        // Skip if no messages to save or no threadId
        if (!messages.length || !threadId) {
          logger.log("AIChatWidget: Not saving messages - no threadId or empty messages array");
          return;
        }

        // Use current state from refs to avoid dependency cycles and infinite loops
        // since this function triggers state updates
        const currentStorageState = storageStateRef.current;
        const currentUiState = uiStateRef.current;

        logger.log("AIChatWidget: Debounced save triggered, saving to storage", {
          threadId,
          messageCount: messages.length,
          storageType: storage.isIndexedDBSupported ? "IndexedDB" : "Memory",
          isInitialized: storage.isInitialized,
        });

        // Always save to memory as a backup
        saveMessagesToMemory(messages);

        // If we're using memory-only mode, don't try IndexedDB
        if (currentStorageState.useMemoryFallback) {
          logger.log("AIChatWidget: Using memory-only mode, not attempting IndexedDB save");
          return;
        }

        try {
          if (!storage.isInitialized || !storage.saveMessages) {
            logger.warn("AIChatWidget: Storage not initialized or saveMessages not available");
            return;
          }

          // Log messages being saved
          logger.log("AIChatWidget: Saving messages to IndexedDB storage:", {
            threadId,
            messageCount: messages.length,
          });

          // Save to storage
          await storage.saveMessages(threadId, messages);
          logger.log("AIChatWidget: Messages saved successfully to IndexedDB");

          // Hide welcome page if we have messages
          if (messages.length > 0 && currentUiState.showWelcomePage) {
            dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: false });
          }
        } catch (error) {
          logger.error("AIChatWidget: Error saving messages to storage:", error);

          // Track storage failures to potentially switch to memory-only mode
          handleStorageFailure(currentStorageState, dispatchStorage, logger);
        }
      }, MESSAGE_SAVE_DEBOUNCE_MS);

      // Store the timer for this threadId
      saveMessagesDebounceTimersRef.current.set(threadId, timer);
    },
    [
      storage,
      // storageState, // Removed to avoid dependency cycle
      saveMessagesToMemory,
      // uiState.showWelcomePage, // Removed to avoid dependency cycle
      dispatchUI,
      dispatchStorage,
    ],
  );

  // Save messages to storage when they change (debounced)
  useEffect(() => {
    logger.log("AIChatWidget: Messages changed, scheduling debounced save", {
      threadId: chatState.threadId,
      messageCount: chatState.messages.length,
    });

    debouncedSaveMessages(chatState.threadId, chatState.messages);
  }, [chatState.messages, chatState.threadId, debouncedSaveMessages]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      // Clear all pending timers on unmount
      saveMessagesDebounceTimersRef.current.forEach((timer) => {
        clearTimeout(timer);
      });
      saveMessagesDebounceTimersRef.current.clear();
    };
  }, []);

  // Clear debounce timer for previous threadId when it changes to prevent saving stale messages
  const previousThreadIdRef = useRef<string | null>(chatState.threadId);
  useEffect(() => {
    const previousThreadId = previousThreadIdRef.current;
    if (previousThreadId !== null && previousThreadId !== chatState.threadId) {
      // Clear any pending save for the old threadId
      const timer = saveMessagesDebounceTimersRef.current.get(previousThreadId);
      if (timer) {
        clearTimeout(timer);
        saveMessagesDebounceTimersRef.current.delete(previousThreadId);
        logger.log("AIChatWidget: Cleared debounce timer for previous threadId", {
          previousThreadId,
          newThreadId: chatState.threadId,
        });
      }
    }
    previousThreadIdRef.current = chatState.threadId;
  }, [chatState.threadId]);

  // Add effect to update CSS variables when colors change
  useEffect(() => {
    document.documentElement.style.setProperty("--primary-color", primaryColor);
    document.documentElement.style.setProperty("--secondary-color", secondaryColor);
  }, [primaryColor, secondaryColor]);

  // Add a timer for updating timestamps
  useEffect(() => {
    // Update timestamps every minute
    const timestampInterval = setInterval(() => {
      updateAllTimestamps();
    }, 60000);

    return () => clearInterval(timestampInterval);
  }, []);

  // Function to toggle theme
  const toggleTheme = () => {
    const newTheme = uiState.theme === "light" ? "dark" : "light";
    dispatchUI({ type: "SET_THEME", payload: newTheme });
    if (onThemeChange) {
      onThemeChange(newTheme);
    }
  };

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      if (initialTheme === undefined) {
        // Only update if theme not explicitly set
        dispatchUI({ type: "SET_THEME", payload: e.matches ? "dark" : "light" });
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    }

    return () => {
      // Clean up
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      }
    };
  }, [initialTheme]);

  // Get theme colors based on current theme and primary/secondary colors
  const currentThemeColors = resolveThemeColors(uiState.theme, primaryColor, secondaryColor);
  const themeStyle = themeToCssVars(currentThemeColors);

  /**
   * Handles opening/closing the chat window
   * Prevents default button behavior and event bubbling
   */
  const toggleChat = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (uiState.isOpen) {
      dispatchUI({ type: "SET_IS_CLOSING", payload: true });
      // State updates will be handled by transitionend event listener in useEffect
    } else {
      dispatchUI({ type: "SET_IS_OPEN", payload: true });
      // Input focusing will be handled by useEffect when isOpen becomes true
    }

    // Scroll to chat button when opening
    if (!uiState.isOpen && chatButtonRef.current) {
      chatButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    }
  };

  /**
   * Handles chat window closing transition with event-driven approach
   * Listens for transitionend event to dispatch state updates when closing animation completes
   */
  useEffect(() => {
    if (!uiState.isClosing || !chatWindowRef.current) {
      return;
    }

    const chatWindow = chatWindowRef.current;
    let fallbackTimeout: ReturnType<typeof setTimeout> | null = null;
    let transitionEndHandler: ((e: TransitionEvent) => void) | null = null;

    const cleanup = () => {
      if (transitionEndHandler) {
        chatWindow.removeEventListener("transitionend", transitionEndHandler);
        transitionEndHandler = null;
      }
      if (fallbackTimeout) {
        clearTimeout(fallbackTimeout);
        fallbackTimeout = null;
      }
    };

    // Handler for transitionend event
    transitionEndHandler = (e: TransitionEvent) => {
      // Only handle transitions on the chat window itself (not child elements)
      if (e.target !== chatWindow) {
        return;
      }

      // Optional: check propertyName if we want to be specific about which transition
      // For now, we accept any transition completion
      cleanup();
      dispatchUI({ type: "SET_IS_OPEN", payload: false });
      dispatchUI({ type: "SET_IS_CLOSING", payload: false });
    };

    // Attach transitionend listener (not using once: true to handle child element transitions correctly)
    chatWindow.addEventListener("transitionend", transitionEndHandler);

    // Fallback timeout in case transitionend doesn't fire (300ms matches CSS transition duration)
    fallbackTimeout = setTimeout(() => {
      cleanup();
      dispatchUI({ type: "SET_IS_OPEN", payload: false });
      dispatchUI({ type: "SET_IS_CLOSING", payload: false });
    }, 300);

    // Cleanup on unmount or when isClosing changes
    return cleanup;
  }, [uiState.isClosing]);

  /**
   * Focuses input field when chat window opens
   * Uses requestAnimationFrame to ensure DOM is ready after opening
   */
  useEffect(() => {
    if (!uiState.isOpen || uiState.showWelcomePage || !inputRef.current) {
      return;
    }

    // Use requestAnimationFrame to ensure element is attached to DOM
    requestAnimationFrame(() => {
      if (!uiState.showWelcomePage && inputRef.current) {
        inputRef.current.focus();
      }
    });
  }, [uiState.isOpen, uiState.showWelcomePage]);

  /**
   * Updates input field value as user types
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatchChat({ type: "SET_INPUT_VALUE", payload: e.target.value });
  };

  /**
   * Handler for sending a message programmatically
   */
  const sendMessage = useCallback(
    async (text: string) => {
      // Hide welcome page if it's currently shown
      if (uiState.showWelcomePage) {
        dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: false });
      }

      const userMessage: Message = {
        id: `user_${generateRandomIdSuffix()}`,
        text,
        type: "user",
        timestamp: new Date(),
        threadId: chatState.threadId,
      };

      // Add message using reducer action to safely append (handles sorting automatically)
      dispatchChat({ type: "ADD_MESSAGE", payload: userMessage });

      dispatchChat({ type: "SET_IS_AI_TYPING", payload: true });

      // Check if we're offline
      if (uiState.isOffline) {
        try {
          let offlineMessageText = "You are currently offline.";

          // Offline message handling is disabled
          logger.log("AIChatWidget: Offline message handling is disabled");
          offlineMessageText = "You are currently offline. Please try again when you reconnect.";

          // Show offline message
          const offlineMessage: Message = {
            id: `bot_offline_${generateRandomIdSuffix()}`,
            text: offlineMessageText,
            type: "bot",
            timestamp: new Date(),
            threadId: chatState.threadId,
          };

          // Add offline message using reducer action to safely append (handles sorting automatically)
          dispatchChat({ type: "ADD_MESSAGE", payload: offlineMessage });

          dispatchChat({ type: "SET_IS_AI_TYPING", payload: false });
          return;
        } catch (error) {
          logger.error("AIChatWidget: Error handling offline message:", error);
          // Continue with normal flow, which will likely fail due to being offline
          // but at least the user will see an error message
        }
      }

      // Send to API with streaming support
      try {
        let threadIdFromStream = chatState.threadId || storageState.memoryThreadId || undefined;
        const botMessageId = `bot_${generateRandomIdSuffix()}`;

        logger.log("[STREAMING] Creating bot message with ID:", botMessageId);
        logger.log("[STREAMING] Current messages count:", chatState.messages.length);

        const assistantApiUrl = buildWidgetApiUrl(apiBaseUrl, appId, "/assistant");
        logger.log("[STREAMING] Fetching from:", assistantApiUrl);

        const response = await fetch(assistantApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-App-Id": appId,
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            message: text,
            threadId: threadIdFromStream,
            stream: true, // Enable streaming
          }),
        });

        logger.log(
          "[STREAMING] Response status:",
          response.status,
          "headers:",
          Object.fromEntries(response.headers.entries()),
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Handle streaming response
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let partialText = "";
        let messageAdded = false; // Track if we've added the bot message

        logger.log("[STREAMING] Starting to read stream for message ID:", botMessageId);

        if (reader) {
          while (true) {
            const { value, done } = await reader.read();
            if (done) {
              logger.log("[STREAMING] Stream reading complete");
              break;
            }

            // Decode chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });
            logger.log("[STREAMING] Buffer content:", buffer);

            // Process complete lines (SSE format)
            const lines = buffer.split("\n");
            buffer = lines.pop() || ""; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith("data: ")) {
                try {
                  const data = JSON.parse(line.substring(6));
                  logger.log("[STREAMING] Parsed event:", data);

                  if (data.type === "threadId") {
                    // Got threadId from stream
                    threadIdFromStream = data.threadId;
                    logger.log("[STREAMING] Received threadId:", data.threadId);
                    dispatchChat({ type: "SET_THREAD_ID", payload: data.threadId });
                    saveThreadIdToMemory(data.threadId);

                    // Save to storage if not using memory fallback
                    if (
                      !storageState.useMemoryFallback &&
                      storage.isInitialized &&
                      storage.saveThreadId
                    ) {
                      try {
                        await storage.saveThreadId(data.threadId);
                        logger.log("ThreadId saved to storage:", data.threadId);
                      } catch (error) {
                        logger.error("Error saving threadId:", error);
                        handleStorageFailure(storageState, dispatchStorage, logger);
                      }
                    }
                  } else if (data.type === "token") {
                    // Accumulate content
                    partialText += data.content;

                    // Add bot message on first token (hide typing indicator and show message)
                    if (!messageAdded) {
                      dispatchChat({ type: "SET_IS_AI_TYPING", payload: false });
                      dispatchChat({
                        type: "ADD_MESSAGE",
                        payload: {
                          id: botMessageId,
                          text: partialText, // Add with first token content
                          type: "bot" as const,
                          timestamp: new Date(),
                          threadId: threadIdFromStream,
                        },
                      });
                      messageAdded = true;
                      logger.log(
                        "[STREAMING] Bot message added on first token with content:",
                        partialText,
                      );
                    } else {
                      // Update existing message with accumulated text
                      logger.log(
                        "[STREAMING] Token received. Partial text now:",
                        partialText.substring(0, 50) + "...",
                      );
                      logger.log(
                        "[STREAMING] Dispatching UPDATE_MESSAGE_TEXT for ID:",
                        botMessageId,
                      );
                      dispatchChat({
                        type: "UPDATE_MESSAGE_TEXT",
                        payload: { id: botMessageId, text: partialText },
                      });
                    }
                  } else if (data.type === "done") {
                    // Stream complete
                    logger.log("[STREAMING] Stream completed successfully");
                  } else if (data.type === "error") {
                    // Error from server
                    throw new Error(data.error || "Stream error");
                  }
                } catch (parseError) {
                  logger.error("[STREAMING] Error parsing SSE data:", parseError, "Line:", line);
                }
              }
            }
          }
        } else {
          logger.error("[STREAMING] No reader available from response body");
        }

        // Ensure typing indicator is off
        dispatchChat({ type: "SET_IS_AI_TYPING", payload: false });
      } catch (error) {
        dispatchChat({ type: "SET_IS_AI_TYPING", payload: false });
        logger.error("Error sending message after retries:", error);
        const errorMessage: Message = {
          id: `bot_error_${generateRandomIdSuffix()}`,
          text: "Sorry, I encountered an error. Please try again later.",
          type: "bot",
          timestamp: new Date(),
          threadId: chatState.threadId,
        };

        // Add error message using reducer action to safely append (handles sorting automatically)
        dispatchChat({ type: "ADD_MESSAGE", payload: errorMessage });
      }
    },
    [
      apiBaseUrl,
      appId,
      chatState.threadId,
      token,
      uiState.showWelcomePage,
      uiState.isOffline,
      storage,
      storageState,
      saveThreadIdToMemory,
    ],
  );

  /**
   * Handles message submission from input form
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatState.inputValue.trim()) return;

    sendMessage(chatState.inputValue);
    dispatchChat({ type: "SET_INPUT_VALUE", payload: "" });
  };

  /**
   * Handler for the "Ask Question" button in welcome page
   */
  const handleAskQuestionClick = () => {
    dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: false });

    // Focus the input field after the DOM updates
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  /**
   * Handler for clicking on a suggested question in welcome page
   */
  const handleSuggestedQuestionClick = (question: string) => {
    sendMessage(question);

    // Focus the input field after sending the message
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);
  };

  // Add method to clear conversation
  const clearConversation = () => {
    dispatchUI({ type: "SET_SHOW_CLEAR_CONFIRM", payload: true });
  };

  const handleConfirmClear = async () => {
    try {
      // Clear messages from state
      dispatchChat({ type: "CLEAR_MESSAGES" });

      // Reset threadId
      dispatchChat({ type: "SET_THREAD_ID", payload: null });

      // Try to clear storage, but don't let it block if it fails
      try {
        if (storage.clearMessages) {
          await storage.clearMessages();
        } else {
          logger.warn(
            "storage.clearMessages is not available - messages will not be cleared from storage",
          );
        }
      } catch (storageError) {
        logger.warn("Failed to clear messages from storage, but continuing:", storageError);
        // This is non-critical, we can continue without clearing storage
      }

      dispatchUI({ type: "SET_SHOW_CLEAR_CONFIRM", payload: false });

      // Show welcome page again
      dispatchUI({ type: "SET_SHOW_WELCOME_PAGE", payload: true });

      // Force scroll check after clearing conversation
      setTimeout(() => {
        if (messagesContainerRef.current) {
          // Trigger scroll event to update the ScrollDownButton visibility
          messagesContainerRef.current.dispatchEvent(new Event("scroll"));
        }
      }, 100);
    } catch (error) {
      logger.error("Error clearing conversation:", error);
      dispatchUI({ type: "SET_SHOW_CLEAR_CONFIRM", payload: false });
    }
  };

  const handleCancelClear = () => {
    dispatchUI({ type: "SET_SHOW_CLEAR_CONFIRM", payload: false });
  };

  const toggleWidth = () => dispatchUI({ type: "TOGGLE_IS_WIDE" });

  // Function to group messages by sender
  const groupMessages = (messages: Message[]): MessageGroup[] => {
    if (!messages.length) return [];

    // Make a defensive copy and ensure messages are sorted by timestamp
    const sortedMessages = [...messages].sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
    );

    logger.log("AIChatWidget: Grouping messages - sorted by timestamp for display");

    const groups: MessageGroup[] = [];
    let currentGroup: MessageGroup | null = null;

    sortedMessages.forEach((message) => {
      // If this is the first message or the sender changed, create a new group
      if (!currentGroup || currentGroup.type !== message.type) {
        if (currentGroup) {
          groups.push(currentGroup);
        }

        currentGroup = {
          type: message.type,
          messages: [message],
          timestamp: message.timestamp,
        };
      } else {
        // Add to existing group
        currentGroup.messages.push(message);
        // Update group timestamp to be the latest message time
        if (message.timestamp.getTime() > currentGroup.timestamp.getTime()) {
          currentGroup.timestamp = message.timestamp;
        }
      }
    });

    // Add the last group if it exists
    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups;
  };

  /**
   * Copy message text to clipboard
   */
  const copyToClipboard = (text: string, messageId: string) => {
    // Use the modern Clipboard API
    navigator.clipboard
      .writeText(stripPatternFromString(text))
      .then(() => {
        // Show copied feedback
        dispatchUI({ type: "SET_COPIED_MESSAGE_ID", payload: messageId });

        // Reset feedback after 2 seconds
        setTimeout(() => {
          dispatchUI({ type: "SET_COPIED_MESSAGE_ID", payload: null });
        }, 2000);
      })
      .catch((err) => {
        logger.error("Failed to copy text: ", err);
      });
  };

  return (
    <div
      className={`ai-chat-widget-container ${position} theme-${uiState.theme}`}
      style={themeStyle}
    >
      {/* Chat Window */}
      {uiState.isOpen && (
        <div
          ref={chatWindowRef}
          className={`chat-window ${uiState.isWide ? "wide" : ""} ${uiState.isClosing ? "closing" : ""}`}
        >
          {/* Chat Header */}
          <div className="chat-header">
            <div className="header-title">
              {botLogo && (
                <div className="bot-logo">
                  <img
                    src={botLogo}
                    alt={`${botName} logo`}
                    onError={(e) => {
                      // Hide image on error
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              <h3>
                {botName}
                {uiState.isOffline && (
                  <span
                    style={{
                      fontSize: "0.7em",
                      backgroundColor: "#f8d7da",
                      color: "#721c24",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      marginLeft: "8px",
                      verticalAlign: "middle",
                    }}
                  >
                    Offline
                  </span>
                )}
              </h3>
            </div>
            <div className="header-buttons-grp">
              {chatState.threadId && (
                <button
                  onClick={clearConversation}
                  className="header-buttons"
                  title="Clear conversation"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 2v6h6" />
                    <path d="M21 12A9 9 0 0 0 6 5.3L3 8" />
                    <path d="M21 22v-6h-6" />
                    <path d="M3 12a9 9 0 0 0 15 6.7l3-2.7" />
                    <circle cx="12" cy="12" r="1" />
                  </svg>
                </button>
              )}

              {/* Add Theme Toggle button */}
              {uiState.enableThemeToggle && (
                <ThemeToggle theme={uiState.theme} toggleTheme={toggleTheme} />
              )}

              <button onClick={toggleWidth} className="header-buttons" title="Toggle width">
                {uiState.isWide ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 3v3a2 2 0 0 1-2 2H3" />
                    <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
                    <path d="M3 16h3a2 2 0 0 1 2 2v3" />
                    <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                    <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
                    <path d="M3 16v3a2 2 0 0 0 2 2h3" />
                    <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                  </svg>
                )}
              </button>
              <button onClick={toggleChat} className="header-buttons" aria-label="Close chat">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
            </div>
          </div>

          {/* Conditional Content: Welcome Page or Chat Messages */}
          {uiState.showWelcomePage ? (
            <WelcomePage
              botName={botName}
              {...(botLogo && { botLogo: botLogo })}
              initialQuestions={initialMessages}
              onQuestionClick={handleSuggestedQuestionClick}
              onAskQuestionClick={handleAskQuestionClick}
              theme={uiState.theme}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            />
          ) : (
            <>
              {/* Messages Container */}
              <div className="chat-messages" ref={messagesContainerRef}>
                {/* Group messages by sender and render groups */}
                {groupMessages(chatState.messages).map((group, groupIndex) => (
                  <div key={groupIndex} className={`message-group ${group.type}`}>
                    {group.messages.map((message, messageIndex) => {
                      // Use stable message id as key to avoid flickering during streaming
                      const uniqueKey = message.id || `msg-${groupIndex}-${messageIndex}`;
                      const messageId = `msg-${groupIndex}-${messageIndex}`;

                      return (
                        <div key={uniqueKey} className={`message ${message.type} message-enter`}>
                          {/* Message content */}
                          <div>{renderSafeFormattedText(message.text)}</div>

                          {/* Footer with timestamp and actions in one row */}
                          <div className="message-footer">
                            <span
                              className="message-timestamp"
                              data-timestamp={message.timestamp.toISOString()}
                            >
                              {formatRelativeTime(message.timestamp)}
                            </span>

                            {message.type === "bot" && (
                              <div className="message-actions">
                                <button
                                  className={`action-button copy-button ${
                                    uiState.copiedMessageId === messageId ? "copied" : ""
                                  }`}
                                  onClick={() => copyToClipboard(message.text, messageId)}
                                  title="Copy to clipboard"
                                >
                                  {uiState.copiedMessageId === messageId ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M20 6L9 17l-5-5"></path>
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"></path>
                                    </svg>
                                  )}
                                </button>
                                {/* Space for future action buttons like thumbs up/down */}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}

                {/* Add this after the existing message groups but before the typing indicator */}
                {chatState.messages.length === 0 && !uiState.showWelcomePage && (
                  <div className="message-group bot">
                    <div className="message bot message-enter">
                      <div>
                        <h3>Hi there, welcome 👋</h3>
                        <p>
                          You're chatting with an AI Agent! I'm not your average chatbot—I can do
                          way more. Just tell me what's on your mind, and I'll do my best to help
                          you out quickly.
                        </p>
                      </div>
                      <div className="message-footer">
                        <span
                          className="message-timestamp"
                          data-timestamp={new Date().toISOString()}
                        >
                          {formatRelativeTime(new Date())}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Enhanced typing indicator */}
                {chatState.isAITyping && (
                  <div className="message bot typing-indicator message-enter">
                    <div className="typing-bubble-container">
                      <div className="typing-bubble"></div>
                      <div className="typing-bubble"></div>
                      <div className="typing-bubble"></div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Scroll Down Button - now outside chat-messages but inside chat window */}
              <ScrollDownButton
                messagesContainerRef={messagesContainerRef}
                onScrollDown={scrollToBottom}
              />

              {/* Input Form - Only show when welcome page is not displayed */}
              <form className="chat-input" onSubmit={handleSubmit}>
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={
                    chatState.isAITyping
                      ? "Please wait..."
                      : uiState.isOffline
                        ? "Offline mode - messages will be sent when online"
                        : "Type a message"
                  }
                  value={chatState.inputValue}
                  onChange={handleInputChange}
                  disabled={chatState.isAITyping}
                  className={`${chatState.isAITyping ? "disabled" : ""} ${uiState.isOffline ? "offline" : ""}`}
                />
                <button
                  type="submit"
                  aria-label="Send message"
                  disabled={chatState.isAITyping || !chatState.inputValue.trim()}
                  className={chatState.isAITyping ? "disabled" : ""}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z" />
                    <path d="M6 12h16" />
                  </svg>
                </button>
              </form>
            </>
          )}

          {/* Attribution Footer */}
          {uiState.enableAttribution && (
            <div className="chat-attribution">
              <a href="https://www.askinfosec.tech" target="_blank" rel="noopener noreferrer">
                Powered by AskInfosec
              </a>
            </div>
          )}

          {uiState.showClearConfirm && (
            <ConfirmDialog
              title="Start new chat"
              message="Are you sure you want to start a new chat and end the current chat?"
              onConfirm={handleConfirmClear}
              onCancel={handleCancelClear}
            />
          )}
        </div>
      )}
      {/* Toggle Button */}
      <button ref={chatButtonRef} className="chat-toggle" onClick={toggleChat}>
        {uiState.isOpen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        )}
      </button>
    </div>
  );
};

export { AIChatWidget };
